import { formatDate, getAllMonths } from 'utils/date.utils';

export const periodOptions = [
  { id: 0, description: 'Current Period' },
];

// #genai - Format currency amount with thousand separators (e.g., "$1,234.56")
export const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'N/A';
  return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// #genai - Format date string for display (e.g., "Jan 1, 2024")
export const formatDateForDisplay = (dateString) => {
  if (!dateString) return 'N/A';
  return formatDate(dateString, 'MMM D, YYYY') || dateString;
};

// #genai - Get the current month name
export const getCurrentMonthName = () => {
  const months = getAllMonths();
  return months[new Date().getMonth()];
};

// #genai - Check if a month is in the future relative to current month
export const isFutureMonth = (month) => {
  const months = getAllMonths();
  const currentMonthIndex = new Date().getMonth();
  const monthIndex = months.indexOf(month);
  return monthIndex > currentMonthIndex;
};

// #genai - Pad historic usage data to always show 12 months with zero values for missing months
export const padHistoricUsageTo12Months = (historicUsage) => {
  const allMonths = getAllMonths();

  if (!historicUsage || historicUsage.length === 0) {
    return allMonths.map((month) => ({
      month,
      qty: 0,
      notStarted: isFutureMonth(month),
    }));
  }

  const dataMap = new Map(historicUsage.map((item) => [item.month, item]));

  return allMonths.map((month) => {
    const existingData = dataMap.get(month);
    if (existingData) {
      return { ...existingData, notStarted: false };
    }
    const template = { ...historicUsage[0] };
    Object.keys(template).forEach((key) => {
      if (key !== 'month') {
        template[key] = 0;
      }
    });
    template.month = month;
    template.notStarted = isFutureMonth(month);
    return template;
  });
};

// #genai - Color palette for products in charts
export const PRODUCT_COLORS = [
  '#88E1D2', // Teal
  '#3E649B', // Blue
  '#7FB8C4', // Light Blue
  '#8FC4D0', // Cyan
  '#A4CED8', // Pale Blue
];

// #genai - SVG pattern URL to CSS gradient mapping for tooltip marks
export const COLOR_MAP = {
  'url(#overage-stripe)': 'repeating-linear-gradient(-45deg, #E9B02D 0px, #E9B02D 3px, #D4A01F 3px, #D4A01F 6px)',
  'url(#inprocess-stripe)': 'repeating-linear-gradient(-45deg, #9BB3CC 0px, #9BB3CC 3px, #E8EBF0 3px, #E8EBF0 6px)',
  'url(#inprocess-1)': `repeating-linear-gradient(-45deg, ${PRODUCT_COLORS[0]} 0px, ${PRODUCT_COLORS[0]} 3px, #b2c0d6 3px, #b2c0d6 6px)`,
  'url(#inprocess-2)': `repeating-linear-gradient(-45deg, ${PRODUCT_COLORS[1]} 0px, ${PRODUCT_COLORS[1]} 3px, #b2c0d6 3px, #b2c0d6 6px)`,
  'url(#inprocess-3)': `repeating-linear-gradient(-45deg, ${PRODUCT_COLORS[2]} 0px, ${PRODUCT_COLORS[2]} 3px, #b2c0d6 3px, #b2c0d6 6px)`,
  'url(#inprocess-4)': `repeating-linear-gradient(-45deg, ${PRODUCT_COLORS[3]} 0px, ${PRODUCT_COLORS[3]} 3px, #b2c0d6 3px, #b2c0d6 6px)`,
};

// #genai - Get CSS style for tooltip mark based on color/pattern (handles both predefined and dynamic patterns)
export const getMarkStyle = (color, series = []) => {
  if (COLOR_MAP[color]) {
    return { background: COLOR_MAP[color] };
  }
  if (color?.startsWith('url(#inprocess-')) {
    const productIdMatch = color.match(/inprocess-(\d+)/);
    if (productIdMatch) {
      const productId = productIdMatch[1];
      const regularSeries = series.find((s) => s.dataKey === productId && !s.dataKey?.includes('_inprocess'));
      if (regularSeries?.color) {
        return { background: `repeating-linear-gradient(-45deg, ${regularSeries.color} 0px, ${regularSeries.color} 3px, #b2c0d6 3px, #b2c0d6 6px)` };
      }
    }
  }
  return { backgroundColor: color };
};

// #genai - Format chart value for display (returns null for zero/falsy values)
export const formatChartValue = (value) => (value ? `${value}` : null);

// #genai - Format month name to 3-letter abbreviation (e.g., "January" -> "Jan")
export const formatMonthName = (month) => month.substring(0, 3);

// #genai
/**
 * Calculate usage statistics based on subscription details and historic usage
 * @param {Array} historicUsage - Array of monthly usage data
 * @param {Object} subscription - Subscription details
 * @returns {Array} - Array of usage statistics for display
 */
export const calculateSubscriptionUsageData = (historicUsage, subscription) => {
  if (!historicUsage || !subscription) return [];

  const {
    includedQty = 0, overageRatePerUnit = 0, monthlyFee = 0, startDate, endDate,
  } = subscription;

  // Calculate totals from historic usage
  const totalUsage = historicUsage.reduce((sum, month) => sum + (Math.min(includedQty, month.qty || 0)), 0);
  const totalOverages = historicUsage.reduce((sum, month) => {
    const overage = Math.max(0, (month.qty || 0) - includedQty);
    return sum + overage;
  }, 0);

  // Calculate fees
  const overageFees = totalOverages * overageRatePerUnit;
  // #genai - Calculate months count from effective start date to current month (or end date if subscription is over)
  const start = new Date(startDate);
  const now = new Date();
  const end = endDate ? new Date(endDate) : now;
  const effectiveEnd = end < now ? end : now;
  const monthsCount = startDate
    ? (effectiveEnd.getFullYear() - start.getFullYear()) * 12
      + (effectiveEnd.getMonth() - start.getMonth()) + 1
    : historicUsage.length;
  const totalSubscriptionFees = monthlyFee * monthsCount;

  return [
    { label: 'Contract to Date Usage', value: totalUsage.toLocaleString() },
    {
      label: 'Contract to Date Spent',
      value: `$${totalSubscriptionFees.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    { label: 'Overages', value: totalOverages.toLocaleString() },
    { label: 'Overage Fees', value: `$${overageFees.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
  ];
};
